import { useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ListOrdered, Loader2, Lock, Save } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ptBR, enUS } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { getApiUrl } from "@/lib/config";

export interface GovBrQueueSigner {
  id: string;
  recipientName: string;
  recipientPhone?: string | null;
  status: string;
  order?: number;
  signedAt?: string | null;
  isGovBrTurn?: boolean;
  govBrTurnSince?: string | null;
  govBrLastSentAt?: string | null;
  govBrUploadedDocUrl?: string | null;
}

interface Props {
  requestId: string;
  /** The signers of one gov.br request, in queue order. */
  signers: GovBrQueueSigner[];
  language: string;
  onSaved: () => void;
}

const isOpen = (s: GovBrQueueSigner) => s.status !== "signed" && s.status !== "declined";

/**
 * gov.br flow: one signer at a time. The sender reorders whoever has not signed yet; signed ones stay on top.
 * Moving someone ahead of the signer in turn sends them the file at once and tells the other one to wait (confirmed
 * first). The signer in turn is locked while the file they returned waits for review.
 */
export function GovBrOrderPanel({ requestId, signers, language, onSaved }: Props) {
  const pt = language === "pt";
  const { toast } = useToast();
  const signed = signers.filter((s) => !isOpen(s));
  const open = signers.filter(isOpen);
  const current = open.find((s) => s.isGovBrTurn) ?? open[0];
  const locked = !!current?.govBrUploadedDocUrl; // returned file waiting for review: stays first
  const serverKey = open.map((s) => `${s.id}:${s.status}:${s.govBrUploadedDocUrl ? 1 : 0}`).join("|");
  const [ids, setIds] = useState<string[]>(open.map((s) => s.id));
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(false);
  useEffect(() => { setIds(open.map((s) => s.id)); }, [serverKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const byId = useMemo(() => new Map(open.map((s) => [s.id, s])), [serverKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const local = ids.map((id) => byId.get(id)).filter((s): s is GovBrQueueSigner => !!s);
  const dirty = ids.join("|") !== open.map((s) => s.id).join("|");
  const next = local[0];
  const turnChanges = !!next && !!current && next.id !== current.id;

  const when = (iso: string) => new Date(iso).toLocaleString(pt ? "pt-BR" : "en-US", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  const ago = (iso: string) => formatDistanceToNow(new Date(iso), { addSuffix: true, locale: pt ? ptBR : enUS });
  const firstMovable = locked ? 1 : 0;
  const canMove = local.length - firstMovable >= 2; // e.g. one returned file waiting for review + one other: nothing to swap
  const move = (index: number, delta: number) => {
    const to = index + delta;
    if (index < firstMovable || to < firstMovable || to >= ids.length) return;
    const copy = [...ids];
    [copy[index], copy[to]] = [copy[to], copy[index]];
    setIds(copy);
  };

  const statusLine = (s: GovBrQueueSigner, localIndex?: number) => {
    if (!isOpen(s)) return { text: `✅ ${pt ? "Assinou" : "Signed"}${s.signedAt ? ` ${pt ? "em" : "on"} ${when(s.signedAt)}` : ""}`, cls: "text-green-700 dark:text-green-400" };
    if (dirty && turnChanges && localIndex === 0) return { text: pt ? "▶ Vai receber o documento ao salvar" : "▶ Gets the document when you save", cls: "text-emerald-700 dark:text-emerald-400 font-medium" };
    if (dirty && turnChanges && s.id === current?.id) return { text: pt ? "🕒 Vai aguardar a vez (recebe um aviso)" : "🕒 Will wait for their turn (gets a notice)", cls: "text-amber-700 dark:text-amber-400 font-medium" };
    if (s.id === current?.id) {
      if (s.govBrUploadedDocUrl) return { text: pt ? "📥 Devolveu o arquivo: aguardando sua revisão. Para passar alguém na frente, aprove ou rejeite antes." : "📥 Returned the file: waiting for your review. To move anyone ahead, approve or reject it first.", cls: "text-amber-700 dark:text-amber-400" };
      const since = s.govBrTurnSince ? `${pt ? "desde" : "since"} ${when(s.govBrTurnSince)} (${ago(s.govBrTurnSince)})` : "";
      const sent = s.govBrLastSentAt
        ? ` · ${pt ? "último envio" : "last sent"} ${when(s.govBrLastSentAt)}`
        : ` · ⚠️ ${pt ? "envio não confirmado, use Reenviar" : "send not confirmed, use Resend"}`;
      return { text: `⏳ ${pt ? "Na vez, com o documento" : "Their turn, has the document"} ${since}${sent}`, cls: "text-blue-700 dark:text-blue-400" };
    }
    return { text: `🕒 ${pt ? "Aguardando a vez" : "Waiting for their turn"}`, cls: "text-muted-foreground" };
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(getApiUrl(`/api/signatures/${requestId}/govbr-order`), {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token") ?? ""}` },
        body: JSON.stringify({ recipientIds: ids }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const code = data?.errors?.[0]?.code as string | undefined;
        const msg = code === "awaiting-review"
          ? (pt ? `${current?.recipientName ?? ""} já devolveu o arquivo: aprove ou rejeite antes de mudar quem assina agora.` : `${current?.recipientName ?? ""} already returned the file: approve or reject it before changing who signs now.`)
          : code === "invalid-order"
            ? (pt ? "A lista mudou enquanto você editava. A página foi atualizada; confira e salve de novo." : "The list changed while you were editing. The page was refreshed; check it and save again.")
            : code === "inactive"
              ? (pt ? "Este documento não está mais em assinatura." : "This document is no longer out for signature.")
              : (data?.message || (pt ? "Não foi possível salvar a ordem." : "Could not save the order."));
        toast({ title: pt ? "Ordem não salva" : "Order not saved", description: msg, variant: "destructive" });
        onSaved();
        return;
      }
      const nextName = data.nextSignerName || next?.recipientName || "";
      toast(data.turnChanged && !data.nextNotified
        ? { title: pt ? "Ordem salva" : "Order saved", description: pt ? `O envio para ${nextName} não foi confirmado. Use o botão de reenviar.` : `The send to ${nextName} was not confirmed. Use the resend button.`, variant: "destructive" }
        : {
            title: pt ? "Ordem salva" : "Order saved",
            description: data.turnChanged
              ? (pt ? `${nextName} recebeu o documento${data.waitNoticeSent && current ? `; ${current.recipientName} foi avisado para aguardar a vez` : ""}.` : `${nextName} got the document${data.waitNoticeSent && current ? `; ${current.recipientName} was told to wait for their turn` : ""}.`)
              : (pt ? "A nova ordem vale a partir de agora." : "The new order applies from now on."),
          });
      onSaved();
    } catch {
      toast({ title: pt ? "Ordem não salva" : "Order not saved", description: pt ? "Sem conexão com o servidor. Tente de novo." : "No connection to the server. Try again.", variant: "destructive" });
    } finally {
      setSaving(false);
      setConfirm(false);
    }
  };

  const rows: { s: GovBrQueueSigner; localIndex?: number }[] = [...signed.map((s) => ({ s })), ...local.map((s, i) => ({ s, localIndex: i }))];
  return (
    <div className="mb-5 rounded-lg border border-emerald-200 bg-emerald-50/60 p-4 dark:border-emerald-900 dark:bg-emerald-950/20" data-testid="govbr-order-panel" onClick={(e) => e.stopPropagation()}>
      <p className="flex items-center gap-2 text-sm font-semibold">
        <ListOrdered className="h-4 w-4 text-emerald-700 dark:text-emerald-400" />
        {pt ? "Ordem de assinatura (gov.br)" : "Signing order (gov.br)"}
      </p>
      <p className="mb-3 mt-0.5 text-xs text-muted-foreground">
        {pt
          ? "Uma pessoa por vez, nesta ordem. Quem já assinou fica fixo; os outros mudam de lugar com as setas."
          : "One person at a time, in this order. Signers who already signed stay put; move the others with the arrows."}
      </p>
      <ol className="space-y-2">
        {rows.map(({ s, localIndex }, i) => {
          const line = statusLine(s, localIndex);
          const li = localIndex ?? -1;
          const movable = canMove && li >= firstMovable;
          const moved = localIndex !== undefined && dirty && open[localIndex]?.id !== s.id;
          return (
            <li key={s.id} data-testid={`govbr-order-row-${s.id}`}
              className={`flex items-center gap-3 rounded-md border bg-background px-3 py-2 ${moved ? "ring-2 ring-emerald-400" : ""}`}>
              <span className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold ${!isOpen(s) ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" : "bg-emerald-600 text-white"}`}>{i + 1}</span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium" data-testid={`govbr-order-name-${i + 1}`}>{s.recipientName}</p>
                <p className={`text-xs ${line.cls}`} data-testid={`govbr-order-status-${s.id}`}>{line.text}</p>
              </div>
              {movable ? (
                <div className="flex flex-shrink-0 gap-1">
                  <Button type="button" size="icon" variant="outline" className="h-8 w-8" disabled={saving || li === firstMovable}
                    onClick={() => move(li, -1)} aria-label={pt ? `Subir ${s.recipientName}` : `Move ${s.recipientName} up`} data-testid={`govbr-order-up-${s.id}`}>
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                  <Button type="button" size="icon" variant="outline" className="h-8 w-8" disabled={saving || li === local.length - 1}
                    onClick={() => move(li, 1)} aria-label={pt ? `Descer ${s.recipientName}` : `Move ${s.recipientName} down`} data-testid={`govbr-order-down-${s.id}`}>
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <Lock className="h-4 w-4 flex-shrink-0 text-muted-foreground" aria-label={pt ? "Posição fixa" : "Fixed position"} />
              )}
            </li>
          );
        })}
      </ol>
      {dirty && (
        <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
          <span className="mr-auto text-xs font-medium text-amber-700 dark:text-amber-400">{pt ? "Nova ordem ainda não salva." : "New order not saved yet."}</span>
          <Button type="button" variant="ghost" size="sm" disabled={saving} onClick={() => setIds(open.map((s) => s.id))} data-testid="govbr-order-undo">{pt ? "Desfazer" : "Undo"}</Button>
          <Button type="button" size="sm" disabled={saving} onClick={() => (turnChanges ? setConfirm(true) : void save())} data-testid="govbr-order-save">
            {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
            {pt ? "Salvar ordem" : "Save order"}
          </Button>
        </div>
      )}
      <Dialog open={confirm} onOpenChange={(o) => { if (!saving) setConfirm(o); }}>
        <DialogContent data-testid="govbr-order-confirm">
          <DialogHeader>
            <DialogTitle>{pt ? "Mudar quem assina agora?" : "Change who signs now?"}</DialogTitle>
            <DialogDescription>{pt ? "Confira o que acontece ao salvar:" : "This is what happens when you save:"}</DialogDescription>
          </DialogHeader>
          {next && current ? (
            <ul className="list-disc space-y-2 pl-5 text-sm">
              <li>
                <strong>{next.recipientName}</strong>{" "}
                {pt
                  ? `passa a ser a vez e recebe agora o documento para assinar (e-mail${next.recipientPhone ? " e WhatsApp" : ""}).`
                  : `signs next and gets the document now (e-mail${next.recipientPhone ? " and WhatsApp" : ""}).`}
              </li>
              <li>
                <strong>{current.recipientName}</strong>{" "}
                {pt
                  ? `${current.govBrLastSentAt ? `já recebeu o documento (${when(current.govBrLastSentAt)})` : "estava na vez"}. Recebe um e-mail para aguardar a vez; até lá, o link que recebeu não aceita o documento.`
                  : `${current.govBrLastSentAt ? `already got the document (${when(current.govBrLastSentAt)})` : "was next"}. They get an e-mail to wait for their turn; until then, the link they got does not take the document.`}
              </li>
              <li>
                {pt
                  ? `Se ${current.recipientName} já assinou o documento antigo, assina de novo o documento novo quando chegar a vez. Um arquivo antigo devolvido não é aprovado sozinho.`
                  : `If ${current.recipientName} already signed the old document, they sign the new one when their turn comes. An old file sent back is not approved automatically.`}
              </li>
              <li>
                {pt ? "Nova ordem: " : "New order: "}
                {[...signed, ...local].map((s, i) => `${i + 1}. ${s.recipientName}`).join(", ")}
              </li>
            </ul>
          ) : null}
          <DialogFooter>
            <Button variant="outline" disabled={saving} onClick={() => setConfirm(false)} data-testid="govbr-order-cancel">{pt ? "Cancelar" : "Cancel"}</Button>
            <Button disabled={saving} onClick={() => void save()} data-testid="govbr-order-confirm-save">
              {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
              {pt ? "Confirmar nova ordem" : "Confirm new order"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
